// Picks a representative colour for an emoji so pieces can be tinted to match it.

export interface Tint {
  hue: number;
  /** 0–100 */
  saturation: number;
}

const SIZE = 24;
/** Below this the emoji reads as grey/black/white and keeps the default look. */
const MIN_SATURATION = 0.18;

const cache = new Map<string, Tint | null>();
let ctx: CanvasRenderingContext2D | null | undefined;

function context(): CanvasRenderingContext2D | null {
  if (ctx !== undefined) return ctx;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = SIZE;
  ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (ctx) {
    ctx.font = `${SIZE * 0.8}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
  }
  return ctx;
}

/**
 * Renders the emoji once at 24×24 and averages its pixels, weighting each by opacity and
 * saturation. A plain average turns muddy because outlines, shading and highlights are
 * mostly grey; weighting lets the emoji's actual colour win. Results are cached per emoji.
 */
export function emojiTint(emoji: string): Tint | null {
  const hit = cache.get(emoji);
  if (hit !== undefined) return hit;

  const c = context();
  let tint: Tint | null = null;
  if (c) {
    c.clearRect(0, 0, SIZE, SIZE);
    c.fillText(emoji, SIZE / 2, SIZE / 2 + 1);
    const { data } = c.getImageData(0, 0, SIZE, SIZE);
    let r = 0;
    let g = 0;
    let b = 0;
    let total = 0;
    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3]! / 255;
      if (alpha < 0.5) continue;
      const pr = data[i]!;
      const pg = data[i + 1]!;
      const pb = data[i + 2]!;
      const max = Math.max(pr, pg, pb);
      const sat = max === 0 ? 0 : (max - Math.min(pr, pg, pb)) / max;
      const weight = alpha * (0.05 + sat * sat);
      r += pr * weight;
      g += pg * weight;
      b += pb * weight;
      total += weight;
    }
    if (total > 0) tint = toTint(r / total, g / total, b / total);
  }
  cache.set(emoji, tint);
  return tint;
}

function toTint(r: number, g: number, b: number): Tint | null {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (s < MIN_SATURATION) return null;
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { hue: Math.round((h * 60 + 360) % 360), saturation: Math.round(Math.min(s, 0.9) * 100) };
}

/** Custom properties consumed by `.tinted` in styles.css; undefined for grey emojis. */
export function tintStyle(emoji: string): Record<string, string | number> | undefined {
  const t = emojiTint(emoji);
  return t ? { "--th": t.hue, "--ts": `${t.saturation}%` } : undefined;
}
